import { createAppSlice } from '.';
import {
  addArticlesToWorkOrder,
  addClientToWorkOrder,
  addParcelToWorkOrder,
  addWorkOrderMetadataResponses,
  createWorkOrder,
  deleteWorkOrder as deleteApiWorkOrder,
  duplicateAnonymousParcelInWorkOrder,
  getWorkOrder,
  getWorkOrderFile,
  getWorkOrders,
  patchArticleInWorkOrder,
  patchParcelInWorkOrder,
  patchWorkOrder,
  postProductUsageInWorkOrder,
  removeArticleFromWorkOrder,
  removeClientFromWorkOrder,
  removeParcelFromWorkOrder,
  removeProductUsageFromWorkOrder,
  sendPDFWorkOrderFile,
} from '../api/api';
import { PHYTO_VIRTUAL_ARTICLE_ID } from '../constants/articleConstants';
import { TASK_STATUS, TaskStatus } from '../constants/taskConstants';
import type { RootState } from '../store';
import { objectMap } from '../utils';
import { ANALYTICS, sendAnalyticsBtnClickEvent } from '../utils/AnalyticsConstants';
import { stringifyFilters } from '../utils/filters';
import { ItemWithPermissions } from '../utils/permissions';
import { transformApiWorkOrder, transformWorkOrderToWorkOrderPayload } from '../utils/work_orders';
import { Article, ArticleId } from './articles';
import { ClusterId } from './clusters';
import { CropId } from './crops';
import { Equipment, EquipmentId } from './equipments';
import {
  archiveObservationsOfAssets,
  observationsArchived,
  observationsArchivedForAssets,
} from './observations/archived';
import { Observation, createObservation, deleteObservation, updateObservation } from './observations/general';
import { ParcelId } from './parcels';
import { DeviceSessionId } from './sessions';
import { alerting, success } from './snacks';
import { TaskTypeId, selectPhytoTaskTypes } from './taskTypes';
import {
  ArticleRange,
  BaseTask,
  Task,
  TaskAssignedUser,
  TaskId,
  TaskMetadata,
  TaskMetadataId,
  TaskPDFConfig,
  TaskParcel,
  TaskProductUsage,
  TaskWaterData,
} from './tasks';
import { UserId } from './users';

export type WorkOrderId = string;

export type WorkOrderArticleGroup = Article & {
  goal_value?: number;
  worked_value?: number;
  range_value?: ArticleRange[];
  computed_worked_value?: number;
  client_cluster_id: ClusterId;
  position?: number;
};

export type ApiWorkOrder = ItemWithPermissions & {
  article_groups?: WorkOrderArticleGroup[];
  client_cluster_ids: ClusterId[];
  completed_tasks: number;
  created_at: string;
  crop_range_id?: CropId;
  device_session_ids?: DeviceSessionId[];
  drivers?: TaskAssignedUser[];
  equipment_instances?: Equipment[];
  from_ts?: string;
  id: WorkOrderId;
  metadata?: TaskMetadata[];
  name?: string;
  observations?: Observation[];
  owner_cluster_id: ClusterId;
  owner_cluster_name: string;
  parcels?: TaskParcel[];
  pdf_config?: TaskPDFConfig;
  product_usages?: TaskProductUsage[];
  progress: number;
  progress_percent: number;
  status: TaskStatus;
  task_ids?: TaskId[];
  tasks?: Task[];
  task_type_id: TaskTypeId;
  to_ts?: string;
  track?: string;
  total_dose?: number;
  total_quantity?: number;
  water_data?: TaskWaterData;
};

export type CreateWorkOrderPayload = {
  article_groups: {
    article_id: ArticleId;
    goal_value?: number;
    worked_value?: number;
    range_value?: ArticleRange[];
    client_cluster_id: ClusterId;
    position?: number;
  }[];
  client_cluster_ids: ClusterId[];
  from_ts?: string;
  owner_cluster_id: ClusterId;
  parcels?: TaskParcel[];
  status: TaskStatus;
  task_payload: Pick<
    Task,
    | 'assigned_to_ids'
    | 'crop_range_id'
    | 'device_session_ids'
    | 'equipment_instance_ids'
    | 'name'
    | 'product_usages'
    | 'task_type_id'
    | 'total_dose'
  > & { metadata: { task_metadata_id: TaskMetadataId; responses?: string[] }[] };
  to_ts?: string;
};

export type WorkOrder = BaseTask & {
  id: WorkOrderId;
  is_work_order: true;
  progress: number;
  progress_percent: number;
  completed_tasks: number;
  observations?: (Observation & { client_cluster_id?: ClusterId })[];
  tasks?: Task[];
};

/** The dates take null to be cleared, which sending nothing at all wouldn't do */
type WorkOrderPatch = Partial<Omit<WorkOrder, 'from_date' | 'to_date'>> & {
  from_date?: string | null;
  to_date?: string | null;
};

type WorkOrderFilters = {
  client_cluster_id?: ClusterId[];
  crop_range_id?: CropId[];
  equipment_instance_id?: EquipmentId[];
  from_completion_date?: string;
  to_completion_date?: string;
  status?: TaskStatus | TaskStatus[];
  task_type_id?: TaskTypeId[];
  user_id?: UserId[];
  parcel_id?: ParcelId[];
  include_undated_tasks?: boolean;
  include_details?: boolean;
  include_computed_metrics?: boolean;
};

type State = {
  workOrders: WorkOrder[];
  lastFetched: WorkOrder | null;
  loading: boolean;
};

const initialState: State = {
  workOrders: [],
  lastFetched: null,
  loading: true,
};

const workOrdersCache = new Map<string, WorkOrder[]>();

function areFiltersCached(filters: WorkOrderFilters) {
  const key = stringifyFilters(filters);
  return workOrdersCache.has(key) ? key : null;
}

function receiveWorkOrder(state: State, workOrder: WorkOrder) {
  state.lastFetched = workOrder;
  state.workOrders = state.workOrders.filter((wo) => wo.id !== workOrder.id);
  state.workOrders.push(workOrder);
}

const workOrdersSlice = createAppSlice({
  name: 'workOrders',
  initialState,
  reducers: (create) => ({
    fetchWorkOrders: create.asyncThunk(
      async (filters: WorkOrderFilters, { signal, dispatch }) => {
        const apiWorkOrders = (await alerting(() => getWorkOrders(filters, signal), { dispatch })) as
          | ApiWorkOrder[]
          | undefined;
        const workOrders = apiWorkOrders?.map(transformApiWorkOrder);
        if (workOrders) {
          const key = stringifyFilters(filters);
          workOrdersCache.set(key, workOrders);
        }
        return workOrders || [];
      },
      {
        pending: (state, { meta: { arg } }) => {
          const key = areFiltersCached(arg);
          if (key) {
            // immediately load cached work orders without pending state
            state.workOrders = workOrdersCache.get(key)!;
          } else {
            state.loading = true;
          }
        },
        fulfilled: (state, { payload }) => {
          state.workOrders = payload;
        },
        settled: (state, { meta }) => {
          // if the request was aborted, another one is coming right after so we don’t hide
          // the loader until the next request completes (or fails)
          if (meta.requestStatus !== 'rejected' || !meta.aborted) {
            state.loading = false;
          }
        },
      },
    ),
    fetchWorkOrder: create.asyncThunk(
      async (workOrderId: WorkOrderId, { dispatch }) => {
        const apiWorkOrder = (await alerting(() => getWorkOrder(workOrderId), { dispatch })) as ApiWorkOrder;
        return transformApiWorkOrder(apiWorkOrder);
      },
      {
        fulfilled: (state, { payload }) => {
          receiveWorkOrder(state, payload);
        },
      },
    ),
    addWorkOrder: create.asyncThunk(
      async (workOrder: Partial<WorkOrder>, { dispatch }) => {
        const workOrderPayload = transformWorkOrderToWorkOrderPayload(workOrder);
        const newWorkOrder = (await alerting(() => createWorkOrder(workOrderPayload), { dispatch })) as ApiWorkOrder;
        success('CreateTaskModal.success_snack', { dispatch });
        sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.CREATION_SUCCESS);
        if (workOrder.device_session_ids?.length) {
          sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.SUBMIT_FORM_WITH_NEW_INTERVENTION);
        }
        return transformApiWorkOrder(newWorkOrder);
      },
      {
        fulfilled: (state, { payload }) => {
          receiveWorkOrder(state, payload);
        },
      },
    ),
    updateWorkOrder: create.asyncThunk(
      async (
        {
          previousWorkOrder,
          workOrder,
          withSnack = true,
        }: { previousWorkOrder: WorkOrder; workOrder: WorkOrderPatch; withSnack?: boolean },
        { dispatch, getState },
      ) => {
        // update work order clients first as the task has to be updated for the articles patch
        if (workOrder.client_cluster_ids) {
          const addedClients =
            workOrder.client_cluster_ids?.filter((id) => !previousWorkOrder.client_cluster_ids.includes(id)) ?? [];
          const addClientPromises = addedClients.map((clientId) =>
            addClientToWorkOrder(previousWorkOrder.id, clientId),
          );

          await alerting(() => Promise.all(addClientPromises), { dispatch });

          const removedClients = previousWorkOrder.client_cluster_ids.filter(
            (id) => !workOrder.client_cluster_ids?.includes(id),
          );
          const removeClientPromises = removedClients.map((clientId) =>
            removeClientFromWorkOrder(previousWorkOrder.id, clientId),
          );

          await alerting(() => Promise.all(removeClientPromises), { dispatch });
        }

        const promises: Promise<unknown>[] = [];
        if (workOrder.parcels) {
          const deletedParcels = (previousWorkOrder.parcels || []).filter(
            (pp) =>
              !workOrder.parcels?.find((p) => pp.parcel_id === p.parcel_id) &&
              (!pp.client_cluster_id || workOrder.client_cluster_ids?.includes(pp.client_cluster_id)),
          );
          for (const parcel of deletedParcels) {
            promises.push(removeParcelFromWorkOrder(previousWorkOrder.id, parcel.parcel_id));
          }

          const addedParcels = workOrder.parcels.filter(
            (p) => !(previousWorkOrder.parcels || []).find((pp) => p.parcel_id === pp.parcel_id),
          );
          for (const parcel of addedParcels) {
            promises.push(addParcelToWorkOrder(previousWorkOrder.id, parcel.parcel_id, parcel));
          }

          const updatedParcels = workOrder.parcels.filter(
            (p) => !addedParcels.find((ap) => p.parcel_id === ap.parcel_id),
          );
          for (const parcel of updatedParcels) {
            const previousParcel = (previousWorkOrder.parcels || []).find((pp) => pp.parcel_id === parcel.parcel_id);
            const previousGoalArea = previousParcel?.goal_area;
            promises.push(
              patchParcelInWorkOrder(previousWorkOrder.id, parcel.parcel_id, {
                ...parcel,
                goal_area: (workOrder.status === TASK_STATUS.TO_DO ? parcel.goal_area : previousGoalArea) ?? null,
                worked_area: parcel.worked_area ?? null,
                details: (parcel.details || []).map((d) => {
                  const previousGoalValue = previousParcel?.details?.find(
                    (pd) => pd.article_id === d.article_id,
                  )?.goal_value;
                  return {
                    ...d,
                    goal_value: (workOrder.status === TASK_STATUS.TO_DO ? d.goal_value : previousGoalValue) ?? null,
                    worked_value: d.worked_value ?? null,
                  };
                }),
              }),
            );
          }
        }

        if (workOrder.metadata) {
          const responses = workOrder.metadata
            .filter((m) => !!m.id && !!m.responses?.length)
            .map((m) => ({ task_metadata_id: m.id!, responses: m.responses }));
          promises.push(addWorkOrderMetadataResponses(previousWorkOrder.id, responses));
        } else {
          const phytoTaskTypes = selectPhytoTaskTypes(getState() as RootState);
          const taskType = phytoTaskTypes.find((t) => t.id === previousWorkOrder.task_type_id);
          if (taskType && workOrder.status && workOrder.status !== previousWorkOrder.status) {
            // set default metadata
            const newMetadata = (taskType?.metadata || [])
              .map((m) => {
                const currentResponses = (
                  previousWorkOrder.metadata.find((tm) => tm.id === m.id)?.responses || []
                ).filter((r) => String(r).length);
                // if no response saved, and default responses apply with new status, use default responses
                if (
                  !currentResponses.length &&
                  m.default_responses &&
                  (!m.default_responses_apply_with_task_status ||
                    m.default_responses_apply_with_task_status.includes(workOrder.status!))
                ) {
                  return { task_metadata_id: m.id, responses: m.default_responses };
                }
                return { task_metadata_id: m.id, responses: currentResponses };
              })
              .filter((m) => !!m.task_metadata_id && !!m.responses?.length);
            promises.push(addWorkOrderMetadataResponses(previousWorkOrder.id, newMetadata));
          }
        }

        if (workOrder.articles) {
          const previousArticles = (previousWorkOrder.articles || []).filter((a) => a.id !== PHYTO_VIRTUAL_ARTICLE_ID);
          const newArticles = workOrder.articles.filter((a) => a.id !== PHYTO_VIRTUAL_ARTICLE_ID);
          const deletedArticles = previousArticles.filter((pa) => !newArticles.find((a) => pa.id === a.id));
          for (const article of deletedArticles) {
            promises.push(removeArticleFromWorkOrder(previousWorkOrder.id, article.id));
          }

          const addedArticles = newArticles.filter((a) => !previousArticles.find((pa) => a.id === pa.id));
          if (addedArticles.length > 0) {
            const addedArticleGroups = addedArticles
              .map((a) =>
                (a.values || []).map((v) => ({
                  article_id: a.id,
                  client_cluster_id: v.client_cluster_id,
                  worked_value: v.worked_value ?? null,
                  range_value: v.range_value ?? [],
                  goal_value: v.goal_value ?? null,
                  position: a.position,
                })),
              )
              .flat();
            promises.push(addArticlesToWorkOrder(previousWorkOrder.id, addedArticleGroups));
          }

          const updatedArticles = workOrder.articles.filter((a) => !addedArticles.find((aa) => aa.id === a.id));
          for (const article of updatedArticles) {
            for (const values of article.values || []) {
              // keep previous goal value when not updating a todo work order
              const previousGoalValue = ((previousArticles || []).find((a) => a.id === article.id)?.values || []).find(
                (v) => v.client_cluster_id === values.client_cluster_id,
              )?.goal_value;
              promises.push(
                patchArticleInWorkOrder(previousWorkOrder.id, article.id, {
                  client_cluster_id: values.client_cluster_id,
                  worked_value: values.worked_value ?? null,
                  goal_value: (workOrder.status === TASK_STATUS.TO_DO ? values.goal_value : previousGoalValue) ?? null,
                  range_value: values.range_value ?? [],
                  position: article.position,
                }),
              );
            }
          }
        }

        const updatedWorkOrder = await alerting(
          async () => {
            // update parcels, articles and metadata in parallel
            await Promise.all(promises);

            // create parcels from anonymous parcels
            if (workOrder.parcels) {
              await Promise.all(
                workOrder.parcels
                  .filter((p) => p.duplicate_anonymous_parcel)
                  .map((p) => previousWorkOrder.parcels?.find((pp) => pp.parcel_id === p.parcel_id))
                  .filter((p) => !!p?.parcel_task_id)
                  .map((p) => duplicateAnonymousParcelInWorkOrder(previousWorkOrder.id, p!.parcel_task_id!)),
              );
            }

            // update work order
            const workOrderPatch = { ...workOrder, from_ts: workOrder.from_date, to_ts: workOrder.to_date };
            let updatedWorkOrder = (await patchWorkOrder(
              previousWorkOrder.id,
              objectMap(workOrderPatch, (v, k) =>
                k === 'parcels' || k === 'metadata' || k === 'articles' || k === 'from_date' || k === 'to_date'
                  ? undefined
                  : v,
              ),
            )) as ApiWorkOrder;

            // for dose calculation, we need to update product doses sequentially (because the last state depends on the previous updates)
            if (workOrder.product_usages) {
              // remove product usages that have been removed from the work order
              const usagesToDelete =
                previousWorkOrder.product_usages?.filter(
                  (pu) => !workOrder.product_usages?.find((wpu) => wpu.product_usage_id === pu.product_usage_id),
                ) || [];
              for (const previousUsage of usagesToDelete) {
                updatedWorkOrder = await removeProductUsageFromWorkOrder(
                  previousWorkOrder.id,
                  previousUsage.product_usage_id,
                );
              }
              // add or update the usages that have been added or updated
              const usagesToUpsert =
                workOrder.product_usages?.filter((pu) => {
                  const productInPrevious = previousWorkOrder.product_usages?.find(
                    (wpu) => wpu.product_usage_id === pu.product_usage_id,
                  );
                  return (
                    !productInPrevious ||
                    pu.dose !== productInPrevious.dose ||
                    pu.batch_number !== productInPrevious.batch_number ||
                    pu.position !== productInPrevious.position ||
                    pu.function !== productInPrevious.function
                  );
                }) || [];
              for (const usage of usagesToUpsert) {
                updatedWorkOrder = await postProductUsageInWorkOrder(
                  previousWorkOrder.id,
                  usage.product_usage_id,
                  usage,
                );
              }
            }

            sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.MODIFIED);
            if (workOrder.status && workOrder.status !== previousWorkOrder.status) {
              sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.STATUS_CHANGE_SUCCESS);
            }
            if (!previousWorkOrder.device_session_ids?.length && workOrder.device_session_ids?.length) {
              sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.SUBMIT_FORM_WITH_NEW_INTERVENTION);
            }

            return updatedWorkOrder;
          },
          { dispatch },
        );

        if (withSnack) {
          success('Tasks.update_snack', { dispatch });
        }

        if (workOrder.status === TASK_STATUS.ARCHIVED && previousWorkOrder.status !== TASK_STATUS.ARCHIVED) {
          observationsArchived(
            {
              workOrderIds: [previousWorkOrder.id],
              taskIds: (previousWorkOrder.tasks || []).map((task) => task.id),
            },
            { dispatch },
          );
        }

        return transformApiWorkOrder(updatedWorkOrder);
      },
      {
        fulfilled: (state, { payload }) => {
          receiveWorkOrder(state, payload);
        },
      },
    ),
    deleteWorkOrder: create.asyncThunk(
      async (workOrderId: WorkOrderId, { dispatch }) => {
        await alerting(() => deleteApiWorkOrder(workOrderId), { dispatch });
        success('Tasks.delete_snack', { dispatch });
        sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.DELETED);
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          state.workOrders = state.workOrders.filter((workOrder) => workOrder.id !== arg);
        },
      },
    ),
    fetchWorkOrderFile: create.asyncThunk(async (workOrderId: WorkOrderId, { dispatch }) => {
      await alerting(() => getWorkOrderFile(workOrderId), { dispatch });
      sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.WORK_ORDERS.PDF_WORK_ORDER_GENERATED);
    }),
    sendWorkOrderFile: create.asyncThunk(async (payload: { workOrderId: WorkOrderId; email: string }, { dispatch }) => {
      await alerting(() => sendPDFWorkOrderFile(payload.workOrderId), { dispatch });
      success('Tasks.send_pdf_snack', { params: { email: payload.email }, dispatch });
    }),
  }),
  extraReducers: (builder) => {
    function upsertObservationInWorkOrder(state: State, observation: Observation) {
      const workOrderId = observation.work_order_id;
      if (workOrderId) {
        const workOrder = state.workOrders.find((wo) => wo.id === workOrderId);
        if (workOrder) {
          workOrder.observations ??= [];
          workOrder.observations = workOrder.observations.filter((obs) => obs.id !== observation.id);
          workOrder.observations.push(observation);
        }
        if (state.lastFetched?.id === workOrderId) {
          state.lastFetched.observations ??= [];
          state.lastFetched.observations = state.lastFetched.observations.filter((obs) => obs.id !== observation.id);
          state.lastFetched.observations.push(observation);
        }
      }
    }
    builder.addCase(createObservation.fulfilled, (state, { payload }) => {
      upsertObservationInWorkOrder(state, payload);
    });
    builder.addCase(updateObservation.fulfilled, (state, { payload }) => {
      upsertObservationInWorkOrder(state, payload);
    });
    builder.addCase(observationsArchivedForAssets, (state, { payload }) => {
      workOrdersCache.clear();
      state.workOrders.forEach((wo) => {
        if (wo.observations) {
          wo.observations = archiveObservationsOfAssets(wo.observations, payload);
        }
      });
      if (state.lastFetched?.observations) {
        state.lastFetched.observations = archiveObservationsOfAssets(state.lastFetched.observations, payload);
      }
    });

    builder.addCase(deleteObservation.fulfilled, (state, { payload }) => {
      state.workOrders.forEach((wo) => {
        wo.observations = wo.observations?.filter((obs) => obs.id !== payload.observation.id);
      });
      if (state.lastFetched?.observations) {
        state.lastFetched.observations = state.lastFetched?.observations?.filter(
          (obs) => obs.id !== payload.observation.id,
        );
      }
    });
  },
  selectors: {
    selectWorkOrdersLoading: (state) => state.loading,
    selectWorkOrders: (state) => state.workOrders,
    selectLastFetchedWorkOrder: (state) => state.lastFetched,
  },
});

export const {
  fetchWorkOrders,
  fetchWorkOrder,
  addWorkOrder,
  updateWorkOrder,
  deleteWorkOrder,
  fetchWorkOrderFile,
  sendWorkOrderFile,
} = workOrdersSlice.actions;
export const { selectLastFetchedWorkOrder, selectWorkOrders, selectWorkOrdersLoading } = workOrdersSlice.selectors;

export default workOrdersSlice.reducer;
